import useIsDetailLoading from "states/detailLoading";
import { TYPO } from "styles/Typo";
import { useStockNewsRader } from "utils/clientAPIs/newsRader";
import { routeToStockDetailPage } from "utils/routing";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { Forward } from "components/common/Icons";
import moment from "moment-timezone";
import { useResearchInfiniteList } from "utils/hooks/ResearchInfinistList";
import ReportThumb from "components/templates/common/components/ReportThumb";
import React from "react";
import { useTabTracking } from "lib/analytics/react/hooks";
export const ListTemp = React.memo(
  ({
    agenda,
    index,
    length,
  }: {
    agenda: any;
    index: number;
    length: number;
  }) => {
    const router = useRouter();
    return (
      <div
        className={`group cursor-pointer flex gap-4 w-full justify-between flex-start items-start py-3 px-4`}
        style={{
          borderBottom:
            index === length - 1 ? "none" : "1px solid var(--hairlineBorder)",
        }}
        onClick={(e) => {
          router.push(
            `/discuss/${
              agenda.url_slug ? `${agenda.url_slug}-${agenda.id}` : agenda.id
            }`
          );
        }}
        data-gtm-id={`sDetail-debate-genius-list`}
      >
        <div className="flex flex-col gap-2">
          <span
            className={`${TYPO.TYPO7_MD} text-gray80  w-fit line-clamp-2 group-hover:text-blue40`}
          >
            {agenda.title}
          </span>
          <span className={`${TYPO.TYPO8_RG} text-gray50  w-fit`}>
            {moment(agenda.issue_at).format("YYYY.M.D")}
          </span>
        </div>

        <ReportThumb
          data={agenda}
          width={84}
          className=" rounded-[6px] shrink-0  w-[84px]"
        />
      </div>
    );
  }
);
ListTemp.displayName = "ListTemp";
function DetailAINewsRaderSummary({ detailData }: { detailData: any }) {
  const router = useRouter();
  const { t } = useTranslation("detail");
  const { data, isLoading, isError } = useStockNewsRader(detailData?.ITEM_CD);
  const formatNewsDate = (d: string) =>
    router.locale === "ko"
      ? moment(d).format("M월 D일")
      : moment(d).format("MMM D");
  const {
    researchList,
    isLoadingInit,
    isLoadingMore,
    isReachingEnd,
    loadMore,
  } = useResearchInfiniteList({
    ticker: detailData?.ITEM_CD_DL,
  });

  const detailLoading = useIsDetailLoading();
  const { trackClick } = useTabTracking("stock_tab_click", [
    "overview",
    "score",
    "fund",
    "feed",
  ]);
  if (isLoading || detailLoading || isLoadingInit) return <div></div>;
  if (isError) return <div></div>;
  if (data?.length === 0 && researchList?.length === 0) return null;
  if (data?.length === 0 && researchList?.length > 0) {
    return (
      <div className="pt-[30px] px-6 flex flex-col gap-4">
        <p className={`${TYPO.TYPO4_SB} text-gray90`}>{t("reports")}</p>
        <div className="rounded-[14px] overflow-hidden flex flex-col bg-gray5 border border-hairline">
          <div className=" bg-white">
            {researchList?.slice(0, 3)?.map((item: any, index: number) => (
              <ListTemp
                key={index}
                agenda={item}
                index={index}
                length={researchList?.slice(0, 3)?.length}
              />
            ))}
          </div>
          <div
            data-gtm-id={"sDetail-report-more-button"}
            className="w-full flex items-center justify-center cursor-pointer px-4 py-[10px] gap-1 border-t border-hairlineBorder"
            onClick={() => {
              router.push(
                `/detail/${detailData?.ITEM_CD_DL}?tab=aireport&sub=report`,
                undefined,
                {
                  shallow: true,
                }
              );
            }}
          >
            <p className={`${TYPO.TYPO7_MD} text-gray80`}>
              {t("viewMore")}
            </p>
            <Forward fill="gray80" width={12} height={12} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="pt-[30px] px-6 flex flex-col gap-4">
      <p className={`${TYPO.TYPO4_SB} text-gray90`}>{t("recentStockNews")}</p>
      <div className="rounded-[14px] overflow-hidden flex flex-col bg-gray5 border border-hairline">
        <div className="px-4 py-3 bg-white">
          <div className="">
            {data?.slice(0, 3)?.map((item: any, index: number) => (
              <div key={index}>
                <div className="flex items-stretch ">
                  <div className="w-[48px] flex flex-col  shrink-0 items-end">
                    <p
                      className={`text-gray60 ${TYPO.TYPO8_RG} whitespace-nowrap`}
                    >
                      {formatNewsDate(item.BASE_DT)}
                    </p>
                    <p
                      className={`${TYPO.TYPO8_RG} ${
                        Number(item?.ADJ_CLOSE_CHG?.toFixed(1)) === 0
                          ? "text-gray70"
                          : item?.ADJ_CLOSE_CHG > 0
                          ? "text-red40"
                          : "text-blue40"
                      }`}
                    >
                      {Number(item?.ADJ_CLOSE_CHG?.toFixed(1)) > 0 ? "+" : ""}
                      {item?.ADJ_CLOSE_CHG?.toFixed(1)}%
                    </p>
                  </div>
                  <div className="px-4">
                    <div
                      className="h-full w-[1px]  relative"
                      style={{
                        background:
                          data?.length === 1
                            ? "linear-gradient(90deg, rgba(229, 232, 235, 0.00) 0%, #E5E8EB 50%, rgba(229, 232, 235, 0.00) 100%)"
                            : index === 0
                            ? "linear-gradient(180deg, rgba(229, 232, 235, 0.00) 0%, #E5E8EB 100%)"
                            : index === data?.slice(0, 3)?.length - 1
                            ? "linear-gradient(180deg, #E5E8EB 0%, rgba(229, 232, 235, 0.00) 100%)"
                            : "var(--hairlineBorder)",
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-1 pb-4">
                    <p
                      className={`${TYPO.TYPO7_MD} text-gray80 whitespace-pre-line`}
                    >
                      {item?.KEYWORD?.split(",").join("\n")}
                    </p>
                    {/* <p className={`${TYPO.TYPO8_RG} text-gray50 line-clamp-1`}>
                      {item.DESCRIPTION}
                    </p> */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div
          data-gtm-id={"sDetail-news-more-button"}
          className="w-full flex items-center justify-center cursor-pointer px-4 py-[10px] gap-1 border-t border-hairlineBorder"
          onClick={() => {
            trackClick("feed", "shortcut", "stock_overview_news");
            router.push(
              `/detail/${detailData?.ITEM_CD_DL}?tab=aireport&sub=news`,
              undefined,
              {
                shallow: true,
              }
            );
          }}
        >
          <p className={`${TYPO.TYPO7_MD} text-gray80`}>
            {t("viewMore")}
          </p>
          <Forward fill="gray80" width={12} height={12} />
        </div>
      </div>
    </div>
  );
}

export default DetailAINewsRaderSummary;
